import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from '@tanstack/react-router';

import { KpiSummary } from './kpi-summary';
import { MetaOverviewDetail } from './meta-overview-panel';
import { PerformanceOverTime } from './performance-over-time';
import { PerformanceChart, type DeltaCard } from '../performance-chart';
import { OverviewTab } from '../tabs/overview-tab';
import { usePerformanceTimeSeriesQuery, metaTimeSeriesQueryOptions, metaIntegrationStatusQueryOptions, metaCampaignOverviewQueryOptions } from '@/lib/query-options';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { usePerformanceData, useScopedCampaigns } from '@/hooks/usePerformanceData';
import { toDisplayAmount, formatMoney } from '@/lib/performance/money';
import { resolveDateWindow, windowInConversionLag } from '@/lib/performance/date-window';
import { isPerfQueryKey } from '@/lib/performance/refresh';
import { PLATFORM_LABEL } from '@/lib/performance/platform';
import { computeMetaAlerts } from '@/lib/performance/meta-alerts';
import { blendMetaIntoChartPoints, metaSyncedThrough } from '@/lib/performance/meta-blend';
import { platformsInScope, type PlatformScope } from '@/lib/performance/types';
import { summariseScope, type MetricsByPlatform, type PlatformMetrics } from '@/lib/performance/kpis';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import {
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCustomRangeAtom,
  consolidatedPerformanceGroupByAtom,
  consolidatedPerformanceComparisonAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
  consolidatedKeywordsSubViewAtom,
} from '@/store';

const ZERO: PlatformMetrics = { spend: 0, results: 0, clicks: 0, impressions: 0 };

/**
 * Shared Overview data (#5971, CP-6). The KPIs widget and the chart widget both
 * consume it; React Query dedupes the underlying fetches by query key, so they
 * render as independent draggable widgets without double-fetching.
 */
function useOverviewData(scope: PlatformScope) {
  const inScope = platformsInScope(scope);
  const googleInScope = inScope.includes('google');
  const metaInScope = inScope.includes('meta');

  // Honor the toolbar's date range through the shared window helper (#6274) so
  // the top block, the Google detail widgets and the Meta series all cover the
  // exact same calendar days.
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const window = useMemo(() => resolveDateWindow(selectedDays, customRange), [selectedDays, customRange]);

  // #6277 — the same campaign scoping as every other Google widget (the old
  // 'all' sentinel hit the account-wide backend branch, which is silently
  // Search-only for its original Goals-baseline consumer, and ignored the
  // campaign filters). Glitch ids: the time-series route maps them server-side.
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const { scopedCampaigns } = useScopedCampaigns({ includeExternal, typeFilter, selectedCampaignIds });
  const googleChartIds = useMemo(() => scopedCampaigns.map((c) => c.id).join(','), [scopedCampaigns]);

  const { symbol } = useCurrencySymbol();
  const currencySymbol = symbol ?? '';

  const { data: googleRows, isLoading: googleLoading } = usePerformanceTimeSeriesQuery(
    'daily',
    `${window.days}d`,
    googleInScope && googleChartIds ? googleChartIds : undefined,
    { startDate: window.start_date, endDate: window.end_date },
  );
  // #6278 — an unconnected Meta integration must not render "Meta €0" KPI lines
  // (an empty series is indistinguishable from a connected zero-spend account).
  // Enabled travels through the factory (#6489) so its org-seeded gate holds.
  const { data: metaStatus } = useQuery(metaIntegrationStatusQueryOptions(metaInScope));
  const metaConnected = metaInScope && !!metaStatus?.is_connected;
  const { data: metaResp, isLoading: metaLoading } = useQuery(
    metaTimeSeriesQueryOptions(window.start_date, window.end_date, metaConnected),
  );

  const metrics = useMemo<MetricsByPlatform>(() => {
    const out: MetricsByPlatform = {};
    if (googleInScope && googleRows) {
      out.google = googleRows.reduce<PlatformMetrics>(
        (acc, r) => ({
          spend: acc.spend + (r.cost ?? 0),
          results: acc.results + (r.conversions ?? 0),
          clicks: acc.clicks + (r.clicks ?? 0),
          impressions: acc.impressions + (r.impressions ?? 0),
        }),
        { ...ZERO },
      );
    }
    if (metaConnected && metaResp) {
      out.meta = metaResp.rows.reduce<PlatformMetrics>(
        (acc, r) => ({
          spend: acc.spend + toDisplayAmount(r.spend_cents, 'meta'),
          results: acc.results + (r.leads ?? 0),
          clicks: acc.clicks + (r.clicks ?? 0),
          impressions: acc.impressions + (r.impressions ?? 0),
        }),
        { ...ZERO },
      );
    }
    return out;
  }, [googleInScope, googleRows, metaConnected, metaResp]);

  const loading = (googleInScope && googleLoading) || (metaConnected && metaLoading);
  return { metrics, googleRows, metaRows: metaResp?.rows, currencySymbol, loading };
}

/** Overview KPI summary tiles — a draggable/resizable widget. */
export function OverviewKpis({ scope }: { scope: PlatformScope }) {
  const { metrics, currencySymbol, loading } = useOverviewData(scope);
  if (loading) {
    // Skeleton tiles instead of a bare "Loading…" (#6281) — a cold multi-chunk
    // GAQL fetch can take many seconds, during which the empty block read as a
    // broken page.
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {['spend', 'impressions', 'clicks', 'conversions'].map((k) => (
          <div key={k} className="rounded-lg border bg-white p-4 shadow-sm">
            <div className="h-3 w-20 animate-pulse rounded bg-gray-100" />
            <div className="mt-2 h-7 w-24 animate-pulse rounded bg-gray-100" />
          </div>
        ))}
      </div>
    );
  }
  return <KpiSummary metrics={metrics} scope={scope} currencySymbol={currencySymbol} />;
}

/** Overview performance-over-time chart — a draggable/resizable widget. */
export function OverviewChart({ scope }: { scope: PlatformScope }) {
  const { googleRows, metaRows, currencySymbol } = useOverviewData(scope);
  // #6276 — the toolbar's Group By now aggregates this chart (it was inert).
  const groupBy = useAtomValue(consolidatedPerformanceGroupByAtom);
  return (
    <PerformanceOverTime
      scope={scope}
      googleRows={googleRows}
      metaRows={metaRows}
      currencySymbol={currencySymbol}
      groupBy={groupBy}
    />
  );
}

/**
 * #6499 — the retired /performance page's headline chart, ported: metric
 * delta-cards double as line toggles, overlapping metric lines on an x-axis
 * only, rich hover breakdown, optimisation/user-edit dot overlays. Google
 * data (the legacy data layer); the cross-platform PerformanceOverTime
 * remains only for the Meta-only scope, which this chart cannot cover.
 */
function GoogleTimeSeriesChart({ scope }: { scope: PlatformScope }) {
  const queryClient = useQueryClient();
  const { symbol } = useCurrencySymbol('google');
  const cs = symbol ?? '';
  // The four volume metrics plot by default (ruling 09-08); the derived
  // ratios stay opt-in.
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(
    () => new Set(['spend', 'impressions', 'clicks', 'conversions']),
  );
  const [showAiActions, setShowAiActions] = useState(true);
  const [showUserEdits, setShowUserEdits] = useState(false);
  const toggleMetric = (key: string) =>
    setSelectedMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key); // never toggle down to zero lines
      } else {
        next.add(key);
      }
      return next;
    });

  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const groupBy = useAtomValue(consolidatedPerformanceGroupByAtom);
  const comparison = useAtomValue(consolidatedPerformanceComparisonAtom);
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);

  // React Query dedupes these against the Google detail grid's identical
  // fetches (the same pattern useOverviewData relies on).
  const perf = usePerformanceData({
    includeExternal,
    tab: 'overview',
    campaignSubView: 'by-type',
    comparisonMode: comparison,
    selectedCampaignIds,
    chartDateRange: selectedDays,
    customRange,
    chartGroupBy: groupBy,
    showUserEdits,
    selectedMetric: 'spend',
    selectedDays,
    typeFilter,
    cs,
  });

  // The KPI row is blended into these tiles (09-08): cost, impressions and
  // clicks sum across platforms with a per-platform split underneath;
  // conversions render the split form and are never merged into one number
  // (#5971's no-false-equivalence rule); the derived ratios — like the chart
  // itself — stay Google-based and say so whenever Meta contributes.
  // Google-only scopes pass the hook's cards through untouched.
  const { metrics, metaRows } = useOverviewData(scope);

  // Leads starts checked once the tile qualifies (Meta connected and
  // contributing — the data arrives async, so the default can't live in the
  // useState initializer). One-shot: an unchecked-by-the-user Leads stays
  // unchecked afterwards.
  const leadsDefaulted = useRef(false);
  const metaQualifies = !!metrics.meta;
  useEffect(() => {
    if (metaQualifies && !leadsDefaulted.current) {
      leadsDefaulted.current = true;
      setSelectedMetrics((prev) => new Set(prev).add('metaLeads'));
    }
  }, [metaQualifies]);

  // Meta's per-day series merges into the chart points (meta-blend.ts): leads
  // as their own toggleable line, and cost / clicks / impressions ADDED into
  // the shared lines so the plot matches the blended tiles beneath it.
  // CPA / ROAS lines stay Google math — their tiles are Google-marked.
  const chartData = useMemo(() => {
    // Gate on Meta qualifying, not on rows existing: a connected Meta with
    // no rows in the window still plots a flat zero Leads line (the same
    // measured-zero rule as every other series).
    if (!metrics.meta || perf.chartData.length === 0) return perf.chartData;
    return blendMetaIntoChartPoints(perf.chartData, metaRows);
  }, [perf.chartData, metaRows, metrics.meta]);

  // The overlay dots ride the primary metric ('spend'); once the spend line
  // blends Meta in, their y-values re-anchor to the merged points so the dots
  // stay ON the line instead of floating at the Google-only value.
  const blendedSpendByDate = useMemo(() => {
    const m = new Map<string, number>();
    if (metrics.meta) {
      for (const p of chartData) m.set(String(p.date), Number(p.spend) || 0);
    }
    return m;
  }, [chartData, metrics.meta]);
  const actionDatesOnChart = useMemo(
    () => (metrics.meta
      ? perf.actionDatesOnChart.map((a) => ({ ...a, value: blendedSpendByDate.get(a.date) ?? a.value }))
      : perf.actionDatesOnChart),
    [perf.actionDatesOnChart, blendedSpendByDate, metrics.meta],
  );
  const userEditDatesOnChart = useMemo(
    () => (metrics.meta
      ? perf.userEditDatesOnChart.map((e) => ({ ...e, value: blendedSpendByDate.get(e.date) ?? e.value }))
      : perf.userEditDatesOnChart),
    [perf.userEditDatesOnChart, blendedSpendByDate, metrics.meta],
  );

  // Data caveats that apply to the CURRENT window only (a permanent caption
  // would be noise). The conversion-reporting lag rides the Total Conversions
  // tile as a tooltip (ruling 09-09); the footnote slot carries only Meta's
  // synced-through gap when the blend cannot cover the window's tail.
  const footnotes = useMemo(() => {
    const notes: string[] = [];
    if (metrics.meta) {
      const lastSynced = metaSyncedThrough(metaRows);
      if (lastSynced && lastSynced < perf.dateRangeFilter.end_date) {
        const label = new Date(`${lastSynced}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        notes.push(`Meta data is synced through ${label}; later days show Google figures only.`);
      }
    }
    return notes;
  }, [perf.dateRangeFilter.end_date, metrics.meta, metaRows]);
  const blendedCards = useMemo(() => {
    const summary = summariseScope(metrics, scope);
    if (summary.platforms.length < 2) return perf.deltaCards;
    const num = (n: number) => Math.round(n).toLocaleString();
    const subFor = (fmtOne: (m: PlatformMetrics) => string) =>
      summary.platforms.map((p) => ({ platform: p, text: `${PLATFORM_LABEL[p]} ${fmtOne(metrics[p]!)}` }));
    const cards: DeltaCard[] = perf.deltaCards.map((card) => {
      switch (card.key) {
        case 'spend':
          return { ...card, displayValue: formatMoney(summary.spend, cs), subLines: subFor((m) => formatMoney(m.spend, cs)) };
        case 'impressions':
          return { ...card, displayValue: num(summary.impressions), subLines: subFor((m) => num(m.impressions)) };
        case 'clicks':
          return { ...card, displayValue: num(summary.clicks), subLines: subFor((m) => num(m.clicks)) };
        case 'conversions':
          // Leads live in their own tile (ruling 09-08) so neither unit
          // renders merged or as a wrapping split headline (#5971); the
          // marker names this tile's basis.
          return { ...card, subLines: [{ platform: 'google' as const, text: 'Google' }] };
        case 'cpc': {
          // CPC merges legally: cost and clicks are the same unit on both
          // platforms. Each platform also shows its own rate underneath.
          const measurable = summary.clicks > 0;
          return {
            ...card,
            displayValue: measurable ? formatMoney(summary.spend / summary.clicks, cs) : undefined,
            unmeasured: !measurable,
            unmeasuredNote: measurable ? undefined : 'No clicks in this period.',
            subLines: subFor((m) => (m.clicks > 0 ? formatMoney(m.spend / m.clicks, cs) : '–')),
          };
        }
        case 'cpa': {
          // Different result units never merge (#5971): the headline stays
          // the Google figure the deltas run on, and the sub-lines carry each
          // platform's own rate with its own unit — Google per conversion,
          // Meta per lead (the parked CPL tile, folded in here).
          const g = metrics.google;
          const m = metrics.meta;
          return {
            ...card,
            subLines: [
              {
                platform: 'google' as const,
                text: g && g.results > 0 ? `${formatMoney(g.spend / g.results, cs)} / conv.` : '– / conv.',
              },
              ...(m
                ? [{
                    platform: 'meta' as const,
                    text: m.results > 0 ? `${formatMoney(m.spend / m.results, cs)} / lead` : '– / lead',
                  }]
                : []),
            ],
          };
        }
        default:
          // ROAS (google conversion values) has no meta counterpart in the
          // data yet; the marker names the basis.
          return { ...card, subLines: [{ platform: 'google' as const, text: 'Google' }] };
      }
    });
    // Meta's result unit gets its own tile beside Conversions, toggling the
    // merged metaLeads series. It exists only while Meta contributes,
    // mirroring the axed-ROAS philosophy.
    if (metrics.meta) {
      const i = cards.findIndex((c) => c.key === 'conversions');
      cards.splice(i + 1, 0, {
        key: 'metaLeads', label: 'Total Leads', kind: 'sum',
        current: metrics.meta.results, previous: 0, delta: 0,
        hasComparison: false, improving: false, isCurrency: false, lowerIsBetter: false,
        displayValue: num(metrics.meta.results),
        subLines: [{ platform: 'meta', text: 'Meta' }],
      });
    }
    return cards;
  }, [metrics, scope, perf.deltaCards, cs]);

  // The conversion-reporting lag rides the Total Conversions tile as a hover
  // disclosure (ruling 09-09), only while the window touches the settling band.
  const conversionLagNote = windowInConversionLag(perf.dateRangeFilter.end_date)
    ? 'Google conversions can take a few days to report, so the most recent days may still be settling.'
    : undefined;
  const deltaCards = useMemo(() => {
    if (!conversionLagNote) return blendedCards;
    return blendedCards.map((c) => (c.key === 'conversions' ? { ...c, tooltip: conversionLagNote } : c));
  }, [blendedCards, conversionLagNote]);

  return (
    <PerformanceChart
      chartData={chartData}
      chartGroupBy={groupBy}
      selectedMetric="spend"
      selectedMetrics={selectedMetrics}
      toggleMetric={toggleMetric}
      deltaCards={deltaCards}
      actionDatesOnChart={actionDatesOnChart}
      userEditDatesOnChart={userEditDatesOnChart}
      showAiActions={showAiActions}
      onToggleAiActions={() => setShowAiActions((v) => !v)}
      showUserEdits={showUserEdits}
      onToggleUserEdits={() => setShowUserEdits((v) => !v)}
      comparisonMode={comparison}
      timeSeriesError={perf.timeSeriesError as Error | null}
      previousTimeSeriesError={perf.previousTimeSeriesError as Error | null}
      hasWeekendDays={perf.hasWeekendDays}
      currencySymbol={cs}
      hideYAxis
      onRetry={() => queryClient.invalidateQueries({ predicate: (q) => isPerfQueryKey(q.queryKey) })}
      footnotes={footnotes}
      // No campaigns at all → nothing was ever measured (the report queries
      // are deliberately disabled, #6275) — say what unlocks the page rather
      // than implying data is on its way (ruling 09-09).
      emptyMessage={perf.activeCampaigns.length === 0 ? 'Create a campaign to see performance here.' : undefined}
    />
  );
}

// The Google detail grid reuses the full perf-v2 Overview widget layout minus
// 'performance-chart' — the cross-platform PerformanceOverTime in the top block
// already covers time-series, so we drop the Google-only duplicate. This key is
// the tab-id under the shell's shared widget-layout atom (see CONSOLIDATED_DEFAULT_LAYOUTS).
const GOOGLE_OVERVIEW_LAYOUT_KEY = 'google-overview';

// perf-v2 OverviewTab navigates by its own tab ids; map them onto the
// consolidated surface's tab ids (ads → creative-ads, keywords/search-terms →
// keywords-terms; the rest are 1:1).
const OVERVIEW_TAB_NAV_MAP: Record<string, string> = {
  overview: 'overview',
  campaigns: 'campaigns',
  ads: 'creative-ads',
  keywords: 'keywords-terms',
  'search-terms': 'keywords-terms',
  audience: 'audience',
  budget: 'budget',
  activity: 'activity',
};

/**
 * Consolidated Overview tab (#5971). A static cross-platform top block (unified
 * KPI summary + performance chart, shown for every scope) followed by the full
 * perf-v2 Google Overview — all its widgets — whenever Google is in scope. The
 * Google detail is fed by the shared usePerformanceData hook (the same data
 * layer /performance-v2 uses), so there is no duplicated computation.
 *
 * Renders OverviewTab's own WidgetGrid; the top block is plain (non-grid), so
 * only one react-dnd provider is ever mounted on this tab.
 */
export function ConsolidatedOverviewPanel({
  scope,
  getTabLayout,
  onReorder,
  onResize,
  onResetTab,
}: {
  scope: PlatformScope;
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
  onResetTab?: (tabId: string) => void;
}) {
  const googleInScope = platformsInScope(scope).includes('google');
  return (
    <div className="space-y-6">
      {/* #6499 — the old performance page's chart leads; the KPI summary
          boxes sit under it. Meta-only scope keeps the cross-platform
          time-series, which the Google-data chart cannot cover. */}
      {/* Google-in-scope blends the KPI row into the chart's tiles; the
          Meta-only scope keeps the standalone cross-platform pair. */}
      {googleInScope ? (
        <GoogleTimeSeriesChart scope={scope} />
      ) : (
        <>
          <OverviewChart scope={scope} />
          <OverviewKpis scope={scope} />
        </>
      )}
      {googleInScope ? (
        <GoogleOverviewDetail scope={scope} getTabLayout={getTabLayout} onReorder={onReorder} onResize={onResize} onResetTab={onResetTab} />
      ) : (
        <MetaOverviewDetail getTabLayout={getTabLayout} onReorder={onReorder} onResize={onResize} onResetTab={onResetTab} />
      )}
    </div>
  );
}

/** The Google-specific Overview widgets, reusing perf-v2's OverviewTab verbatim. */
function GoogleOverviewDetail({
  scope,
  getTabLayout,
  onReorder,
  onResize,
  onResetTab,
}: {
  scope: PlatformScope;
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
  onResetTab?: (tabId: string) => void;
}) {
  const { symbol } = useCurrencySymbol('google');
  const cs = symbol ?? '';
  const navigate = useNavigate();
  const setKeywordsSubView = useSetAtom(consolidatedKeywordsSubViewAtom);

  // Cross-platform alerts (09-08): Meta's client-side rules merge into the
  // card whenever Meta is in scope and connected; each alert carries its
  // platform so the card can mark rows individually.
  const metaInScope = platformsInScope(scope).includes('meta');
  const { data: metaStatus } = useQuery({ ...metaIntegrationStatusQueryOptions(), enabled: metaInScope });
  const metaConnected = metaInScope && !!metaStatus?.is_connected;
  const { data: metaCampaignsResp } = useQuery(metaCampaignOverviewQueryOptions(metaConnected));

  // Driven by the shared filter toolbar (#5971); tab='overview' gates the
  // Overview-only queries (insights, recommendations, conversion actions, …).
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const groupBy = useAtomValue(consolidatedPerformanceGroupByAtom);
  const comparison = useAtomValue(consolidatedPerformanceComparisonAtom);
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);

  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);

  const perf = usePerformanceData({
    includeExternal,
    tab: 'overview',
    campaignSubView: 'by-type',
    comparisonMode: comparison,
    selectedCampaignIds,
    chartDateRange: selectedDays,
    customRange,
    chartGroupBy: groupBy,
    showUserEdits: false,
    selectedMetric: 'conversions',
    selectedDays,
    typeFilter,
    cs,
  });

  return (
    <OverviewTab
      accountSnapshot={perf.accountSnapshot}
      problemAlerts={[...perf.problemAlerts, ...(metaConnected ? computeMetaAlerts(metaCampaignsResp?.rows ?? [], cs) : [])]}
      spendTrends={perf.spendTrends}
      insightsData={perf.insightsData}
      isInsightsLoading={perf.isInsightsLoading}
      isSearchTermsLoading={perf.isSearchTermsLoading}
      isConversionActionsLoading={perf.isConversionActionsLoading}
      notableSearchTermsByCampaign={perf.notableSearchTermsByCampaign}
      conversionMixByAction={perf.conversionMixByAction}
      totals={perf.totals}
      previousTotals={perf.previousTotals}
      typeMetrics={perf.typeMetrics}
      costByType={perf.costByType}
      convByType={perf.convByType}
      conversionActionsData={perf.conversionActionsData}
      searchTermsData={perf.searchTermsData}
      adMetrics={perf.adMetrics}
      actionLogs={perf.actionLogs}
      budgetUtilisation={perf.budgetUtilisation}
      filteredCampaigns={perf.filteredCampaigns}
      currencySymbol={cs}
      dateRangeLabel={perf.dateRangeLabel}
      onNavigateTab={(t) => {
        // "View all" on search terms must land ON the Search terms sub-view,
        // not the Keywords default (09-08).
        if (t === 'search-terms' || t === 'keywords') setKeywordsSubView(t === 'search-terms' ? 'search-terms' : 'keywords');
        // The consolidated page serves at /performance-beta; the old
        // /performance-consolidated target hit a redirect shim that DROPPED
        // the hash, so every View-all link bounced back to the default tab.
        navigate({ to: '/performance-beta', hash: OVERVIEW_TAB_NAV_MAP[t] ?? 'overview' });
      }}
      // Time-series lives in the top block's cross-platform chart, so the
      // Google grid omits 'performance-chart' from its layout and this is never called.
      renderChart={() => null}
      layout={getTabLayout(GOOGLE_OVERVIEW_LAYOUT_KEY)}
      onReorder={(l) => onReorder(GOOGLE_OVERVIEW_LAYOUT_KEY, l)}
      onResize={(id, w) => onResize(GOOGLE_OVERVIEW_LAYOUT_KEY, id, w)}
      onResetLayout={onResetTab ? () => onResetTab(GOOGLE_OVERVIEW_LAYOUT_KEY) : undefined}
    />
  );
}
